import PropTypes from 'prop-types'
import React, { useMemo, useState, useContext, createContext, useEffect } from 'react'

import AuthApiService from '@apis/auth-api-service'
import UserApiService from '@apis/user-api-service'
import { Alert, Loader } from '@components/ui'
import CONTACTS from '@constants/contacts'
import { identify, updateProfile } from '@utils/mixpanel'

const authContext = createContext()

export function AuthProvider({ children, initialize }) {
  const [user, setUser] = useState(null)

  const { login } = AuthApiService

  const { logout } = AuthApiService

  const hydrateCurrentUser = () =>
    UserApiService.fetchCurrentUser().then((currentUser) => {
      identify(currentUser.id)
      setUser(currentUser)
    })

  const updateUser = (params) => {
    setUser({ ...user, ...params })

    const { firstName, lastName, email, npn, residentState } = params
    updateProfile({ firstName, lastName, email, npn, residentState })
  }

  useEffect(() => {
    if (initialize) {
      hydrateCurrentUser()
    }
  }, [])

  const auth = useMemo(() => ({
    user,
    updateUser,
    login,
    logout,
    hydrateCurrentUser,
  }))

  return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialize: PropTypes.bool,
}

AuthProvider.defaultProps = {
  initialize: false,
}

const useAuth = () => useContext(authContext)

export function EnsureUser({ children }) {
  const { user, hydrateCurrentUser } = useAuth()
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (!user) {
      hydrateCurrentUser().catch(() => setHasError(true))
    }
  }, [])

  if (hasError) {
    return (
      <Alert severity="error">
        There was an error loading. Please refresh or contact us at {CONTACTS.supportEmail}.
      </Alert>
    )
  }

  return user ? children : <Loader />
}

EnsureUser.propTypes = {
  children: PropTypes.node.isRequired,
}

export default useAuth
